import React, { useState } from "react";
import { Input, Button, Form, Col, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import AffiliateTable from "./AffiliateTable";

// Custom Hooks
import useTableController from "hooks/useTableController";

// Validation
import { CategoryBrandFilterSchema } from "model/_base/filters/filter-options.schema";

// API
import { useGetAffiliateProductsQuery, useLazyExportAffiliateProductsQuery } from "services/products/api";
import { useGetFilterOptionsQuery } from "services/filters/api";

// Utils
import { downloadFile, generateTimestampedFilename } from "utils/fileUtils";

// Icons
import { ReactComponent as XlsIcon } from "assets/icons/xls.svg";

const AffiliateProductsTab = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  // HOOK CHANGE
  const { formik, searchInput, setSearchInput, handleTableChange, handleFieldChange } = useTableController({
    initialValues: {
      filterType: "all",
      categoryId: "",
      brandId: "",
    },
    validationSchema: CategoryBrandFilterSchema,
  });

  // State for filter options
  const [options, setOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // API
  const { data: filterOptionsData } = useGetFilterOptionsQuery();
  const { data, error, isLoading } = useGetAffiliateProductsQuery(formik.values);
  const [exports] = useLazyExportAffiliateProductsQuery();

  // Update state when filterOptionsData changes
  React.useEffect(() => {
    if (filterOptionsData) {
      setOptions(filterOptionsData.options || []);
      setCategories(filterOptionsData.categories || []);
      setBrands(filterOptionsData.brands || []);
    }
  }, [filterOptionsData]);

  // ACTIONS
  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);
  const toggleCategoryDropdown = () => setCategoryDropdownOpen((prevState) => !prevState);
  const toggleBrandDropdown = () => setBrandDropdownOpen((prevState) => !prevState);

  const handleOptionSelect = (option) => {
    handleFieldChange("filterType", option.id);
    if (option.id === "category") {
      handleFieldChange("categoryId", categories[0].id);
    } else {
      handleFieldChange("brandId", brands[0].id);
    }
  };

  const handleCategorySelect = (category) => {
    handleFieldChange("categoryId", category.id);
  };

  const handleBrandSelect = (brand) => {
    handleFieldChange("brandId", brand.id);
  };

  // Get selected option, category and brand based on formik values
  const selectedOption = options.find((o) => o.id === formik.values.filterType) || options[0];
  const selectedCategory = categories.find((c) => c.id === formik.values.categoryId) || categories[0];
  const selectedBrand = brands.find((b) => b.id === formik.values.brandId) || brands[0];

  // EXPORT
  const onExport = async () => {
    try {
      const response = await exports(formik.values);
      const fileName = generateTimestampedFilename("products-affiliate");
      await downloadFile(response.data, fileName);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // RENDER
  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="affiliate-container" data-testid="tab-affiliate">
        <div className="header mt-4">
          <h2 className="fw-semibold">สินค้าที่อยู่ใน F&N Affiliate</h2>
          <div className="d-flex flex-column gap-2">
            <Row className="gy-3">
              <Col sm={5} md={6} lg={3} xl={2}>
                <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                  <DropdownToggle caret>{selectedOption?.name || "เลือกตัวเลือก"}</DropdownToggle>
                  <DropdownMenu>
                    {options.map((option) => (
                      <DropdownItem key={option.id} onClick={() => handleOptionSelect(option)} className={`${formik.values.filterType === option.id ? "active" : ""}`}>
                        {option.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </Col>
              <Col sm={7} md={6} lg={3} xl={3}>
                {formik.values.filterType === "category" ? (
                  <Dropdown isOpen={categoryDropdownOpen} toggle={toggleCategoryDropdown}>
                    <DropdownToggle caret>{selectedCategory?.name || "เลือกหมวดหมู่"}</DropdownToggle>
                    <DropdownMenu>
                      {categories.map((category) => (
                        <DropdownItem key={category.id} onClick={() => handleCategorySelect(category)} className={`${formik.values.categoryId === category.id ? "active" : ""}`}>
                          {category.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : formik.values.filterType === "brand" ? (
                  <Dropdown isOpen={brandDropdownOpen} toggle={toggleBrandDropdown}>
                    <DropdownToggle caret>{selectedBrand?.name || "เลือกแบรนด์"}</DropdownToggle>
                    <DropdownMenu>
                      {brands.map((brand) => (
                        <DropdownItem key={brand.id} onClick={() => handleBrandSelect(brand)} className={`${formik.values.brandId === brand.id ? "active" : ""}`}>
                          {brand.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : (
                  <></>
                )}
              </Col>
              <Col sm={12} md={12} lg={6} xl={{ offset: 1, size: 6 }}>
                <div className="d-flex gap-3">
                  <div className="flex-grow-1">
                    <div className="input-group-icon">
                      <i className="bx bx-search" />
                      <Input name="searchTerm" type="text" placeholder="ค้นหารายการสินค้า" onChange={(e) => setSearchInput(e.target.value)} value={searchInput} data-testid="search-input" invalid={formik.touched.searchTerm && formik.errors.searchTerm} />
                      {formik.touched.searchTerm && formik.errors.searchTerm && (
                        <div className="error-message" data-testid="search-error">
                          {formik.errors.searchTerm}
                        </div>
                      )}
                    </div>
                  </div>

                  <Button color="success" size="xs" onClick={onExport} className="d-flex gap-2" data-testid="export-button">
                    <XlsIcon />
                    EXPORT EXCEL
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <AffiliateTable data={data} error={error} isLoading={isLoading} onTableChange={handleTableChange} />
      </div>
    </Form>
  );
};

export default AffiliateProductsTab;
